<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Historial de Billing</h1>
    </v-row>

    <v-alert v-if="error" type="error" dense outlined dismissible class="mb-3">{{ error }}</v-alert>

    <!-- Filtros año/mes -->
    <v-chip-group v-model="filtroAnio" column class="mb-1">
      <v-chip value="" filter outlined small>Todos</v-chip>
      <v-chip v-for="a in anios" :key="a" :value="a" filter outlined small>{{ a }}</v-chip>
    </v-chip-group>

    <v-chip-group v-if="filtroAnio" v-model="filtroMes" column class="mb-3">
      <v-chip value="" filter outlined small>Todos</v-chip>
      <v-chip
        v-for="(m, i) in mesesNombre"
        :key="i"
        :value="String(i + 1).padStart(2, '0')"
        filter outlined small
      >{{ m }}</v-chip>
    </v-chip-group>

    <!-- Filtros empresa -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-select
          v-model="filtroEmpresa"
          :items="empresasDisponibles"
          label="Empresa"
          dense outlined hide-details clearable
          prepend-inner-icon="mdi-domain"
        />
      </v-col>
    </v-row>

    <!-- Tabla (columnas igual a Odie) -->
    <v-data-table
      :headers="headers"
      :items="filteredRows"
      :loading="loading"
      :items-per-page="50"
      class="elevation-1"
      :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
    >
      <!-- Contrato -->
      <template v-slot:item.contratoCodigo="{ item }">
        <span class="font-weight-medium">{{ item.contratoCodigo || '—' }}</span>
      </template>

      <!-- MRR Local (número sin simbolo, como Odie) -->
      <template v-slot:item.mrrLocal="{ item }">
        {{ item.mrrLocal != null ? Math.round(item.mrrLocal).toLocaleString('es-CL') : '—' }}
      </template>

      <!-- Tipo Cambio -->
      <template v-slot:item.tipoCambio="{ item }">
        <span class="grey--text text--darken-1">
          {{ item.tipoCambio != null ? item.tipoCambio.toLocaleString('es-CL') : '—' }}
        </span>
      </template>

      <!-- Movimiento (estado del contrato) -->
      <template v-slot:item.movimiento="{ item }">
        <v-chip
          v-if="item.movimiento"
          x-small
          :color="movimientoColor(item.movimiento)"
          dark
        >{{ item.movimiento }}</v-chip>
        <span v-else class="grey--text">—</span>
      </template>

      <!-- Acciones: Editar | X  (igual a Odie) -->
      <template v-slot:item.actions="{ item }">
        <v-btn text x-small color="primary" class="mr-1 text-capitalize" @click="openEdit(item)">
          <v-icon x-small left>mdi-pencil</v-icon>Editar
        </v-btn>
        <v-btn icon x-small @click="confirmDelete(item)">
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Dialog editar -->
    <v-dialog v-model="dialog" max-width="600" @keydown.esc="dialog = false">
      <v-card>
        <v-card-title class="text-h6">Editar registro</v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.companyId"
                  :items="companies"
                  item-text="name"
                  item-value="id"
                  label="Empresa *"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="form.currency"
                  :items="monedas"
                  label="Moneda *"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="form.priceBilling"
                  label="MRR Local *"
                  type="number"
                  :step="form.currency === 'CLP' ? 1 : 0.01"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="form.yearBilling"
                  label="Año *"
                  type="number"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model.number="form.monthBilling"
                  :items="mesesSelect"
                  label="Mes *"
                  dense outlined
                  :rules="[v => !!v || 'Requerido']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.contratoId"
                  :items="contratos"
                  item-text="codigo"
                  item-value="id"
                  label="Contrato"
                  dense outlined clearable
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.notas"
                  label="Notas"
                  rows="2"
                  dense outlined
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitEdit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog confirmar eliminación -->
    <v-dialog v-model="deleteDialog" max-width="400" @keydown.esc="deleteDialog = false">
      <v-card>
        <v-card-title class="text-h6">Eliminar registro</v-card-title>
        <v-card-text>
          ¿Seguro que quieres eliminar el registro de
          <strong>{{ deletingItem && deletingItem.empresa }}</strong>
          ({{ deletingItem && deletingItem.fecha }})?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" :loading="deleting" @click="executeDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BillingService from '../services/billing.service';
import ContratoService from '../services/contrato.service';
import CompanyService from '../services/companie.service';

export default {
  name: 'BillingHistorial',
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      error: null,
      rows: [],
      companies: [],
      contratos: [],
      dialog: false,
      deleteDialog: false,
      editingId: null,
      deletingItem: null,
      form: {
        companyId: null,
        priceBilling: null,
        currency: 'CLP',
        monthBilling: null,
        yearBilling: null,
        contratoId: null,
        notas: '',
      },
      filtroAnio: String(new Date().getFullYear()),
      filtroMes: String(new Date().getMonth() + 1).padStart(2, '0'),
      filtroEmpresa: null,
      monedas: ['CLP', 'USD', 'PEN', 'MXN'],
      mesesNombre: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
      mesesSelect: [
        { text: 'Enero', value: 1 }, { text: 'Febrero', value: 2 }, { text: 'Marzo', value: 3 },
        { text: 'Abril', value: 4 }, { text: 'Mayo', value: 5 }, { text: 'Junio', value: 6 },
        { text: 'Julio', value: 7 }, { text: 'Agosto', value: 8 }, { text: 'Septiembre', value: 9 },
        { text: 'Octubre', value: 10 }, { text: 'Noviembre', value: 11 }, { text: 'Diciembre', value: 12 },
      ],
      // Columnas en el mismo orden que Odie
      headers: [
        { text: 'CONTRATO',    value: 'contratoCodigo', sortable: true },
        { text: 'EMPRESA',     value: 'empresa',        sortable: true },
        { text: 'FECHA',       value: 'fecha',          sortable: true },
        { text: 'MONEDA',      value: 'monedaLocal',    sortable: false },
        { text: 'MRR LOCAL',   value: 'mrrLocal',       sortable: true, align: 'right' },
        { text: 'TIPO CAMBIO', value: 'tipoCambio',     sortable: false, align: 'right' },
        { text: 'MOVIMIENTO',  value: 'movimiento',     sortable: false },
        { text: '',            value: 'actions',        sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    anios() {
      return [...new Set(this.rows.map(r => r.fecha?.slice(0, 4)).filter(Boolean))].sort().reverse();
    },
    empresasDisponibles() {
      return [...new Set(this.rows.map(r => r.empresa).filter(Boolean))].sort();
    },
    filteredRows() {
      return this.rows
        .filter(r => !this.filtroAnio    || r.fecha?.startsWith(this.filtroAnio))
        .filter(r => !this.filtroMes     || r.fecha?.slice(5, 7) === this.filtroMes)
        .filter(r => !this.filtroEmpresa || r.empresa === this.filtroEmpresa);
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      const [histRes, contRes, compRes] = await Promise.all([
        BillingService.getHistory(),
        ContratoService.getAll(),
        CompanyService.getCompanies(1, 500, ''),
      ]);
      if (histRes.status === 'ok') this.rows = histRes.data;
      else this.error = 'No se pudo cargar el historial.';
      if (contRes.status === 'ok') this.contratos = contRes.data;
      if (compRes.status === 'ok') this.companies = compRes.data?.companies || compRes.data || [];
      this.loading = false;
    },

    openEdit(item) {
      this.editingId = item.id;
      this.form = {
        companyId:    item.companyId,
        priceBilling: item.mrrLocal,
        currency:     item.monedaLocal,
        yearBilling:  parseInt(item.fecha?.slice(0, 4), 10),
        monthBilling: parseInt(item.fecha?.slice(5, 7), 10),
        contratoId:   item.contratoId || null,
        notas:        item.notas || '',
      };
      this.dialog = true;
    },

    async submitEdit() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.error = null;
      const res = await BillingService.update(this.editingId, this.form);
      if (res.status === 'ok') {
        this.dialog = false;
        await this.loadAll();
      } else {
        this.error = 'No se pudo guardar el registro.';
      }
      this.saving = false;
    },

    confirmDelete(item) {
      this.deletingItem = item;
      this.deleteDialog = true;
    },

    async executeDelete() {
      this.deleting = true;
      this.error = null;
      const res = await BillingService.remove(this.deletingItem.id);
      if (res.status === 'ok') {
        this.deleteDialog = false;
        this.deletingItem = null;
        await this.loadAll();
      } else {
        this.error = 'No se pudo eliminar el registro.';
        this.deleteDialog = false;
      }
      this.deleting = false;
    },

    movimientoColor(status) {
      const map = {
        'Activo':  'teal darken-1',
        'Pausado': 'orange darken-1',
        'Churned': 'grey darken-1',
      };
      return map[status] || 'grey darken-1';
    },
  },
};
</script>
